// ============================================
// Public reconstitution calculator (marketing tool, G2M batch 1)
//
// Lead-gen surface at /tools/reconstitution-calculator. Decided by
// Lawrence 2026-07-12; scoped per G2M plan finding F1:
//   - NO database reads: anon users cannot read peptides_free, and we
//     do not widen anon access before the premium REVOKE switch.
//     Presets below are hardcoded COMMON VIAL SIZES + water amounts
//     (technique-level info), never dose recommendations. The user
//     enters their own target dose from their label or provider.
//   - All math reuses src/utils/calculations.ts (safety-critical,
//     36+ unit tests). Never re-implement dosing math here.
// ============================================

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { usePrerenderSignal } from '../../../utils/usePrerenderSignal';
import { trackEvent } from '../../../utils/analytics';
import {
  calculateConcentration,
  calculateDosesPerVial,
  calculateUnitsToFill,
} from '../../../utils/calculations';

// Local display formatter: the app's formatNumber/formatUnits round to the
// nearest HALF unit (practical syringe pips), which would show a 0.25-unit
// draw as "0 units" here. The marketing calculator shows the raw math at
// 2 decimals and warns separately when a draw is too small to measure.
const formatRaw = (n: number): string => String(Math.round(n * 100) / 100);
import '../LandingPage.css';
import './funnel.css';
import { SITE_ORIGIN, appStoreLive, captureTrafficSource, getTrafficSource, isAutomatedBrowser } from './marketingConfig';
import {
  FunnelAppStoreBadge,
  FunnelEmailCapture,
  FunnelFooter,
  FunnelInlineCapture,
  FunnelNav,
  FunnelPartnerBanner,
  FunnelStickyCta,
  FunnelTrustStrip,
} from './MarketingCta';

const BOTTOM_CAPTURE_ID = 'get-the-cheat-sheet';
const CHEAT_SHEET = { name: 'Reconstitution Cheat Sheet', href: '/downloads/reconstitution-cheat-sheet.pdf' };

// Common commercial vial sizes + commonly used water amounts.
// Technique-level presets only - the target dose is always the user's own.
const PRESETS = [
  { label: '5 mg / 2 mL', vialMg: 5, waterMl: 2 },
  { label: '10 mg / 2 mL', vialMg: 10, waterMl: 2 },
  { label: '5 mg / 2.5 mL', vialMg: 5, waterMl: 2.5 },
  { label: '10 mg / 2.5 mL', vialMg: 10, waterMl: 2.5 },
  { label: '50 mg / 3 mL', vialMg: 50, waterMl: 3 },
] as const;

// Input sanity bounds - explicit messages, never silent clamping.
const VIAL_MG_MIN = 0.1;
const VIAL_MG_MAX = 1000;
const WATER_ML_MIN = 0.25;
const WATER_ML_MAX = 30;
const SYRINGE_MAX_UNITS_U100 = 100;

const PAGE_SLUG = 'recon-calculator';
const PATH = '/tools/reconstitution-calculator';

const ReconCalculatorPage: React.FC = () => {
  usePrerenderSignal();

  const [presetIdx, setPresetIdx] = useState<number | null>(null);
  const [vialMg, setVialMg] = useState('');
  const [waterMl, setWaterMl] = useState('');
  const [dose, setDose] = useState('');
  const [doseUnit, setDoseUnit] = useState<'mcg' | 'mg'>('mcg');
  const trackedResult = useRef(false);

  useEffect(() => {
    captureTrafficSource();
    if (isAutomatedBrowser()) return; // prerender/bot traffic: no analytics
    void trackEvent('marketing_page_view', { page: PAGE_SLUG, source: getTrafficSource(PAGE_SLUG) });
  }, []);

  const applyPreset = (i: number) => {
    setPresetIdx(i);
    setVialMg(String(PRESETS[i].vialMg));
    setWaterMl(String(PRESETS[i].waterMl));
  };

  const parsed = useMemo(() => {
    const v = parseFloat(vialMg);
    const w = parseFloat(waterMl);
    const dRaw = parseFloat(dose);
    const dMcg = doseUnit === 'mg' ? dRaw * 1000 : dRaw;
    return { v, w, dMcg, haveAll: vialMg !== '' && waterMl !== '' && dose !== '' };
  }, [vialMg, waterMl, dose, doseUnit]);

  const validation = useMemo(() => {
    const { v, w, dMcg, haveAll } = parsed;
    if (!haveAll) return null;
    if (!Number.isFinite(v) || !Number.isFinite(w) || !Number.isFinite(dMcg)) return 'Those numbers do not look right. Check each field.';
    if (v < VIAL_MG_MIN || v > VIAL_MG_MAX) return `Vial size should be between ${VIAL_MG_MIN} and ${VIAL_MG_MAX} mg. Check your label.`;
    if (w < WATER_ML_MIN || w > WATER_ML_MAX) return `Water amount should be between ${WATER_ML_MIN} and ${WATER_ML_MAX} mL.`;
    if (dMcg <= 0) return 'Your target dose needs to be more than zero.';
    if (dMcg > v * 1000) return 'That dose is more than the whole vial holds. Double-check the dose and the unit (mg vs mcg).';
    return null;
  }, [parsed]);

  const result = useMemo(() => {
    const { v, w, dMcg, haveAll } = parsed;
    if (!haveAll || validation) return null;
    const units = calculateUnitsToFill(dMcg, v, w);
    const mcgPerUnit = calculateConcentration(v, w);
    const dosesPerVial = calculateDosesPerVial(v, dMcg);
    return { units, mcgPerUnit, dosesPerVial };
  }, [parsed, validation]);

  useEffect(() => {
    if (result && !trackedResult.current) {
      trackedResult.current = true;
      // `mode` cleanly separates preset-vs-custom peptide selection; `preset`
      // keeps the specific label (or 'custom') for finer slicing.
      void trackEvent('calculator_result_viewed', {
        page: PAGE_SLUG,
        preset: presetIdx !== null ? PRESETS[presetIdx].label : 'custom',
        mode: presetIdx !== null ? 'preset' : 'custom',
      });
    }
  }, [result, presetIdx]);

  const canonical = `${SITE_ORIGIN}${PATH}`;
  const overSyringe = result !== null && result.units > SYRINGE_MAX_UNITS_U100;
  const underHalfUnit = result !== null && result.units > 0 && result.units < 0.5;

  return (
    <div className="landing fp-page">
      <Helmet>
        <title>Free Peptide Reconstitution Calculator | PRTCL</title>
        <meta name="description" content="How many units do I draw? Enter your vial size, your bacteriostatic water, and your target dose. Get the exact units on a U-100 insulin syringe. Free, no account needed." />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content="Free Peptide Reconstitution Calculator | PRTCL" />
        <meta property="og:description" content="Enter your vial, your water, and your target dose. See the exact units to draw. Free, no account needed." />
        <meta property="og:image" content={`${SITE_ORIGIN}/images/og-landing-v2.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="1200" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Free Peptide Reconstitution Calculator | PRTCL" />
        <meta name="twitter:description" content="Enter your vial, your water, and your target dose. See the exact units to draw. Free, no account needed." />
        <meta name="twitter:image" content={`${SITE_ORIGIN}/images/og-landing-v2.png`} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          name: 'PRTCL Peptide Reconstitution Calculator',
          url: canonical,
          applicationCategory: 'HealthApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          description: 'Free calculator that converts vial size, bacteriostatic water, and target dose into exact insulin-syringe units.',
        })}</script>
      </Helmet>

      <FunnelNav page={PAGE_SLUG} />
      <FunnelPartnerBanner />

      <section className="lp-hero fp-hero">
        <div className="lp-hero-inner fp-hero-inner">
          <div className="lp-hero-copy fp-hero-copy">
            <span className="lp-section-head__eyebrow">MIXING MATH MADE SIMPLE</span>
            <h1>
              How many units do I draw? <span className="lp-accent">Just ask.</span>
            </h1>
            <p className="lp-hero-lede">
              mg, mcg, units, U-100, mL. You should not need a math degree to read your own
              syringe. Tell it your vial, your water, and the dose you and your provider
              already chose, and it shows the syringe units for those numbers. Free, right
              here, no account needed.
            </p>
          </div>
        </div>
      </section>

      <div className="fp-calc-shell">
        <div className="fp-calc-card">
          <div className="fp-calc-presets" role="group" aria-label="Common vial sizes">
            {PRESETS.map((p, i) => (
              <button
                key={p.label}
                type="button"
                className={`fp-calc-preset ${presetIdx === i ? 'is-active' : ''}`}
                onClick={() => applyPreset(i)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <p className="fp-calc-hint">
            Presets fill a common vial size and water amount. Always match what is on YOUR label,
            and get your target dose from your own label or provider.
          </p>

          <div className="fp-calc-fields" style={{ marginTop: 16 }}>
            <div className="fp-calc-field">
              <label htmlFor="calc-vial">Peptide in the vial (mg)</label>
              <input
                id="calc-vial"
                type="number"
                inputMode="decimal"
                min={VIAL_MG_MIN}
                max={VIAL_MG_MAX}
                step="any"
                placeholder="5"
                value={vialMg}
                onChange={(e) => { setVialMg(e.target.value); setPresetIdx(null); }}
              />
              <p className="fp-calc-hint">The mg number printed on the vial label.</p>
            </div>
            <div className="fp-calc-field">
              <label htmlFor="calc-water">Bacteriostatic water added (mL)</label>
              <input
                id="calc-water"
                type="number"
                inputMode="decimal"
                min={WATER_ML_MIN}
                max={WATER_ML_MAX}
                step="any"
                placeholder="2"
                value={waterMl}
                onChange={(e) => { setWaterMl(e.target.value); setPresetIdx(null); }}
              />
              <p className="fp-calc-hint">How much water you mixed in (or plan to).</p>
            </div>
            <div className="fp-calc-field">
              <label htmlFor="calc-dose">Your target dose</label>
              <div className="fp-calc-input-row">
                <input
                  id="calc-dose"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="any"
                  placeholder="250"
                  value={dose}
                  onChange={(e) => setDose(e.target.value)}
                />
                <select
                  className="fp-calc-unit"
                  aria-label="Dose unit"
                  value={doseUnit}
                  onChange={(e) => setDoseUnit(e.target.value as 'mcg' | 'mg')}
                >
                  <option value="mcg">mcg</option>
                  <option value="mg">mg</option>
                </select>
              </div>
              <p className="fp-calc-hint">From your own label or provider. 1 mg = 1000 mcg.</p>
            </div>
          </div>

          {validation && <p className="fp-calc-error" role="alert">{validation}</p>}

          {result && (
            <div className="fp-calc-result" role="status">
              <div className="fp-calc-result__units">
                {formatRaw(result.units)}<span>units on a U-100 insulin syringe</span>
              </div>
              <p className="fp-calc-result__meta">
                Each unit on your syringe now holds about {formatRaw(result.mcgPerUnit)} mcg.
                At this dose, your vial holds about {result.dosesPerVial} doses.
                {overSyringe && ' Heads up: that is more than a full 1 mL syringe (100 units). Double-check your numbers, or the vial may need less water or the dose a second draw.'}
                {underHalfUnit && ' Heads up: that is below half a unit, which is hard to measure reliably on an insulin syringe. Double-check the dose and the unit (mg vs mcg), or mix with less water so the draw is bigger.'}
              </p>
            </div>
          )}

          {/* Peak-intent capture at the result moment (finding F7). */}
          {result && (
            <div className="fp-calc-capture">
              <p className="fp-calc-capture__prompt">Email me this result plus the reconstitution cheat sheet.</p>
              <FunnelInlineCapture
                page={PAGE_SLUG}
                sourceKey="calculator-result"
                placement="calculator_result"
                buttonLabel="Email it to me"
                ariaLabel="Email me this result plus the reconstitution cheat sheet"
                magnet={CHEAT_SHEET}
                successLead="Sent. Your result and the cheat sheet are on the way."
                nextStep={{ label: 'Or start with Peptides 101', to: '/learn' }}
              />
              {appStoreLive() && (
                <div className="fp-calc-capture__store">
                  <p className="fp-calc-capture__store-line">PRTCL remembers this for every dose.</p>
                  <FunnelAppStoreBadge size="lg" page={PAGE_SLUG} />
                </div>
              )}
            </div>
          )}

          <p className="fp-calc-note">
            PRTCL does not recommend, prescribe, or decide doses. This tool only converts the
            numbers you enter (your vial, your water, and a dose you and your healthcare provider
            have already chosen) into syringe units. Educational only, not medical advice. Always
            verify with your provider before acting on it. Using a blend or a stack?
            The PRTCL app handles multi-peptide math, dose logging, and reminders. Questions about
            what is normal when mixing?{' '}
            <Link to="/reconstitution">Read the reconstitution guide</Link> or{' '}
            <Link to="/learn">start with Peptides 101</Link>.
          </p>
        </div>
      </div>

      {/* Source-citation trust strip: social proof, homepage-parity (finding F3). */}
      <FunnelTrustStrip />

      <FunnelEmailCapture
        id={BOTTOM_CAPTURE_ID}
        page={PAGE_SLUG}
        headline="Keep the math by the fridge."
        lede="Get the free Reconstitution Cheat Sheet: the mixing rules and the water-to-dose math for common vial sizes, on one page. You will also get short notes and new guides as we publish them. PRTCL itself is on the App Store now, free to download."
        magnet={CHEAT_SHEET}
        nextStep={{ label: 'Or start with Peptides 101', to: '/learn' }}
      />

      <FunnelFooter />

      {/* Sticky mobile CTA (finding F2). */}
      <FunnelStickyCta page={PAGE_SLUG} label="Get the free cheat sheet" targetId={BOTTOM_CAPTURE_ID} />
    </div>
  );
};

export default ReconCalculatorPage;
